import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { ExternalLinkIcon } from '@heroicons/vue/solid';
import { MetaIntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import {
    MetaConnectionStatusResult,
    MetaPhoneNumber,
    MetaWhatsAppNumberPolicy,
    MetaWhatsAppNumberPolicyPhone,
    WhatsAppWabaStatusColorMap,
} from '@/types';
import CompanyPhoneQuotaSummary from './components/CompanyPhoneQuotaSummary.vue';
import WhatsAppMetaHeader from './components/WhatsAppMetaHeader.vue';
import WhatsAppMetaConfirmDialog from './components/WhatsAppMetaConfirmDialog.vue';
import WhatsAppMetaPhoneActivationDialog from './components/WhatsAppMetaPhoneActivationDialog.vue';
import WhatsAppMetaPhoneNumberListItem from './components/WhatsAppMetaPhoneNumberListItem.vue';
import { attendanceUrl } from '@/configs';
import { wabaStatus } from '@/utils/whatsappMeta';

const perPage = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppMetaDetailPage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();
const uiStore = useUiStore();
const waba = ref<MetaConnectionStatusResult | null>(null);
const policy = ref<MetaWhatsAppNumberPolicy | null>(null);
const busy = ref(false);
const search = ref('');
const phonePage = ref(1);
const phoneToActivate = ref<MetaPhoneNumber | null>(null);
const isDisconnectOpen = ref(false);
const phoneToDisable = ref<MetaWhatsAppNumberPolicyPhone | null>(null);
const wabaId = computed(() => String(route.params.wabaId));
const metaManagerUrl = computed(
    () =>
        `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${waba.value?.businessId || ''}`,
);
const externalLinks = computed(() =>
    [
        { href: metaManagerUrl.value, label: t('whatsappMeta.detail.manageNumbersInMeta') },
        { href: attendanceUrl, label: t('whatsappMeta.list.openAttendance') },
    ].filter((link) => link.href),
);
const setLoading = (value: boolean) => uiStore.setIsLoading(value);
const digits = (value: string) => value.replace(/\D/g, '');
const emptyPolicy = (): MetaWhatsAppNumberPolicy => ({
    version: 0,
    used: 0,
    limit: null,
    full: false,
    phones: [],
});
const hasMoreThanTenPhones = computed(() => (waba.value?.phoneNumbers.length || 0) > perPage);
const isQuotaAvailable = computed(() => !policy.value?.full || policy.value.limit === null);
const shouldPrepareIntegration = computed(
    () =>
        Boolean(waba.value) &&
        !waba.value?.disconnectedAt &&
        (!waba.value?.systemUserAssigned || !waba.value?.webhookSubscribed),
);

const filteredPhones = computed(() => {
    if (!waba.value) return [];

    const value = search.value.trim().toLocaleLowerCase(locale.value);
    if (!value) return waba.value.phoneNumbers;

    const numeric = digits(value);
    return waba.value.phoneNumbers.filter((phone) =>
        [phone.displayPhoneNumber, phone.verifiedName, phone.phoneNumberId].some(
            (item) =>
                item &&
                (item.toLocaleLowerCase(locale.value).includes(value) ||
                    (numeric && digits(item).includes(numeric))),
        ),
    );
});

const pagePhones = computed(() => {
    if (!hasMoreThanTenPhones.value) return filteredPhones.value;
    return filteredPhones.value.slice((phonePage.value - 1) * perPage, phonePage.value * perPage);
});

const phonePolicy = (phone: MetaPhoneNumber): MetaWhatsAppNumberPolicyPhone | null =>
    policy.value?.phones.find(
        (item) => item.wabaId === wabaId.value && item.phoneNumberId === phone.phoneNumberId,
    ) ?? null;

const status = computed(() => (waba.value ? wabaStatus(waba.value) : null));

const load = async () => {
    setLoading(true);
    try {
        const wabaResponse = await MetaIntegrationService.getWaba(wabaId.value);
        waba.value = wabaResponse.data;
        policy.value = waba.value.disconnectedAt
            ? emptyPolicy()
            : (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const prepareIntegration = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.activateWaba(wabaId.value)).data;
        toast.success(t('whatsappMeta.detail.integrationPrepared'));
        await load();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const synchronize = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.synchronizeWaba(wabaId.value)).data;
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t('whatsappMeta.detail.numbersSynchronized'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const activate = async (pin: string) => {
    if (!phoneToActivate.value?.phoneNumberId || !policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (
            await MetaIntegrationService.activatePhoneNumber(
                wabaId.value,
                phoneToActivate.value.phoneNumberId,
                pin,
                policy.value.version,
            )
        ).data;
        phoneToActivate.value = null;
        await load();
        toast.success(t('whatsappMeta.detail.numberActivated'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const setEnabled = async (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (!policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.setWhatsAppPhoneApproval(
            wabaId.value,
            phone.id,
            enabled,
            policy.value.version,
        );
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t(enabled ? 'whatsappMeta.quota.enabled' : 'whatsappMeta.quota.disabled'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const requestEnabled = (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (enabled) return setEnabled(phone, true);
    phoneToDisable.value = phone;
};

const confirmDisable = async () => {
    if (!phoneToDisable.value) return;
    await setEnabled(phoneToDisable.value, false);
    phoneToDisable.value = null;
};

const disconnect = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.disconnectWaba(wabaId.value);
        toast.success(t('whatsappMeta.detail.disconnected'));
        isDisconnectOpen.value = false;
        router.push({ name: 'ListWhatsAppMeta' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

watch(search, () => (phonePage.value = 1));
onMounted(load);

const __returned__ = { route, router, locale, t, uiStore, waba, policy, busy, search, phonePage, phoneToActivate, isDisconnectOpen, phoneToDisable, perPage, wabaId, metaManagerUrl, externalLinks, setLoading, digits, emptyPolicy, hasMoreThanTenPhones, isQuotaAvailable, shouldPrepareIntegration, filteredPhones, pagePhones, phonePolicy, status, load, prepareIntegration, synchronize, activate, setEnabled, requestEnabled, confirmDisable, disconnect, get ExternalLinkIcon() { return ExternalLinkIcon }, get WhatsAppWabaStatusColorMap() { return WhatsAppWabaStatusColorMap }, CompanyPhoneQuotaSummary, WhatsAppMetaHeader, WhatsAppMetaConfirmDialog, WhatsAppMetaPhoneActivationDialog, WhatsAppMetaPhoneNumberListItem }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})